import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "@/api";
import { PageTitle } from "@/components/ui-kit";
import { downloadFile } from "@/lib/download";
import { MapPin, Search, ClipboardCheck, MessageSquareText, FileDown, AlertTriangle, Phone, Stethoscope, TrendingUp, TrendingDown, Award, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import VisitDialog from "@/components/supervisor/VisitDialog";
import MessageBank from "@/components/supervisor/MessageBank";
import VisitsLog from "@/components/supervisor/VisitsLog";
import PlanDialog from "@/components/supervisor/PlanDialog";
import RemedialPlans from "@/components/supervisor/RemedialPlans";

const ST = {
  green: { label: "منجزة", dot: "bg-emerald-500", ring: "border-emerald-400/60", chip: "bg-emerald-500/15 text-emerald-700" },
  yellow: { label: "متوسطة", dot: "bg-amber-400", ring: "border-amber-400/60", chip: "bg-amber-500/15 text-amber-700" },
  red: { label: "متأخرة", dot: "bg-red-500", ring: "border-red-400/60", chip: "bg-red-500/15 text-red-700" },
};

const Stat = ({ label, value, tid, tone = "text-sky-800" }) => (
  <div className="rounded-2xl bg-white/80 border border-sky-200 p-3 sm:p-4" data-testid={tid}>
    <div className={`text-xl sm:text-2xl font-bold ${tone}`}>{value}</div>
    <div className="text-xs text-stone-500 mt-1">{label}</div>
  </div>
);

export const Delta = ({ v, unit = "" }) => {
  if (v === null || v === undefined) return null;
  if (v === 0) return <span className="text-[10px] text-stone-400">بلا تغيير</span>;
  const up = v > 0;
  return <span className={`text-[10px] inline-flex items-center gap-0.5 ${up ? "text-emerald-700" : "text-red-700"}`}>{up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{up ? "+" : ""}{v}{unit}</span>;
};

const SchoolCard = ({ s, threshold, readOnly, onVisit, onMsg, onPlan }) => {
  const st = ST[s.status];
  return (
    <div data-testid={`my-school-${s.id}`} className={`rounded-2xl bg-white/85 border-2 ${st.ring} p-4 flex flex-col gap-2`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-stone-800 text-sm leading-6">{s.name}</div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1 shrink-0 ${st.chip}`} data-testid={`my-school-status-${s.id}`}>
          <span className={`w-2 h-2 rounded-full ${st.dot}`} />{st.label}
        </span>
      </div>
      <div className="text-xs text-stone-500 flex items-center gap-1">{s.coordinator_name || "بلا منسق"}{s.coordinator_phone && <><Phone size={11} className="mr-2" />{s.coordinator_phone}</>}</div>
      <div className="grid grid-cols-4 gap-1 text-center text-xs">
        <div><div className="font-bold text-sky-800">{s.participation_rate}%</div><div className="text-stone-500">المشاركة</div><Delta v={s.delta_rate} unit="%" /></div>
        <div><div className="font-bold text-sky-800">{s.hours}</div><div className="text-stone-500">ساعة</div><Delta v={s.delta_hours} /></div>
        <div><div className="font-bold text-sky-800">{s.executed_opportunities}</div><div className="text-stone-500">فرصة منفذة</div></div>
        <div><div className="font-bold text-sky-800">{s.volunteers}</div><div className="text-stone-500">متطوع</div></div>
      </div>
      {s.inactive && <div className="text-[11px] text-red-700 flex items-center gap-1"><AlertTriangle size={12} />
        {s.inactive_days == null ? "لم تُدخل أي نشاط بعد" : `لا نشاط منذ ${s.inactive_days} يوم`} (أكثر من {threshold} أيام)</div>}
      {s.open_plans > 0 && <div className="text-[11px] text-amber-700 flex items-center gap-1"><Stethoscope size={12} /> خطة علاجية مفتوحة</div>}
      <div className="text-[11px] text-stone-400">آخر نشاط: {s.last_activity || "—"} · المتابعات: {s.visits_count}{s.last_visit ? ` (آخرها ${s.last_visit})` : ""}</div>
      {!readOnly && (
        <div className="flex gap-1.5 mt-1">
          <button data-testid={`my-school-visit-${s.id}`} onClick={() => onVisit(s)} className="flex-1 btn-gold py-2 rounded-xl text-xs flex items-center justify-center gap-1"><ClipboardCheck size={14} /> متابعة</button>
          <button data-testid={`my-school-msg-${s.id}`} onClick={() => onMsg(s)} className="flex-1 py-2 rounded-xl text-xs border border-sky-400/50 text-sky-800 bg-sky-50 hover:bg-sky-100 flex items-center justify-center gap-1"><MessageSquareText size={14} /> رسالة</button>
          {s.status !== "green" && !s.open_plans && <button data-testid={`my-school-plan-${s.id}`} onClick={() => onPlan(s)} className="flex-1 py-2 rounded-xl text-xs border border-red-400/50 text-red-700 bg-red-50 hover:bg-red-100 flex items-center justify-center gap-1"><Stethoscope size={14} /> خطة علاجية</button>}
        </div>
      )}
    </div>
  );
};

export default function MySchools() {
  const { supId } = useParams();
  const readOnly = !!supId;
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("schools");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [visitFor, setVisitFor] = useState(null);
  const [msgFor, setMsgFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);

  const load = () => {
    if (readOnly) {
      api.get(`/oversight/supervisors/${supId}`).then((r) => { setData(r.data); setVisits(r.data.visits || []); setPlans(r.data.plans || []); }).catch((e) => toast.error(apiError(e)));
      return;
    }
    api.get("/my-schools").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/my-visits").then((r) => setVisits(r.data)).catch(() => {});
    api.get("/my-plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [supId]);

  const schools = useMemo(() => data?.schools || [], [data]);
  const shown = useMemo(() => schools.filter((s) =>
    (filter === "all" || (filter === "alerts" ? s.inactive : s.status === filter)) && (!q.trim() || s.name.includes(q.trim()))
  ), [schools, filter, q]);
  const sm = data?.summary;
  const openPlans = plans.filter((p) => p.status === "open").length;

  const report = async (fmt) => {
    try { await downloadFile(`/my-schools/report?format=${fmt}`, `تقرير-إنجاز-نطاقي.${fmt}`); }
    catch { toast.error("تعذّر تنزيل التقرير"); }
  };

  const chips = [["all", "الكل", sm?.schools], ["red", "متأخرة", sm?.red], ["yellow", "متوسطة", sm?.yellow], ["green", "منجزة", sm?.green], ["alerts", `بلا نشاط ${data?.inactive_days_threshold || 7} أيام`, sm?.alerts]];
  const title = readOnly ? `لوحة المشرف: ${data?.supervisor?.name || "…"}` : "متابعة مدارسي";

  return (
    <div className="space-y-5" data-testid="my-schools-page">
      {readOnly && <Link to="/supervisors-oversight" data-testid="back-to-oversight" className="text-sm text-sky-700 inline-flex items-center gap-1"><ArrowRight size={15} /> العودة لمتابعة المشرفين</Link>}
      <PageTitle title={title} subtitle={readOnly ? "عرض للاطلاع فقط — كما يراها المشرف" : "المدارس المرتبطة بحسابك فقط — مؤشراتها، تنبيهاتها، خططها العلاجية وسجل متابعاتك"} icon={MapPin}
        actions={!readOnly && <div className="flex flex-wrap gap-2">
          <button data-testid="report-pdf" onClick={() => report("pdf")} disabled={!sm?.schools} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-1 disabled:opacity-50"><FileDown size={15} /> تقرير إنجاز نطاقي PDF</button>
          <button data-testid="report-achievement" onClick={() => downloadFile("/my-schools/achievement", "تقرير-إنجازي.pdf").catch(() => toast.error("تعذّر تنزيل التقرير"))} disabled={!sm?.schools} className="px-4 py-2 rounded-xl text-sm bg-sky-600 text-white flex items-center gap-1 disabled:opacity-50"><FileDown size={15} /> تقرير إنجازي (صفحة واحدة)</button>
          <button data-testid="report-xlsx" onClick={() => report("xlsx")} disabled={!sm?.schools} className="px-4 py-2 rounded-xl text-sm border border-emerald-500/50 text-emerald-700 bg-emerald-50 flex items-center gap-1 disabled:opacity-50"><FileDown size={15} /> Excel</button>
        </div>} />

      {data?.rank && <div data-testid="sup-rank" className="rounded-2xl bg-gradient-to-l from-amber-100 to-white border border-amber-300 px-4 py-3 text-sm text-stone-700 flex items-center gap-2">
        <Award size={18} className="text-amber-600" /> الترتيب بين المشرفين: <b className="text-amber-700">{data.rank.rank}</b> من {data.rank.of} (حسب لقطة {data.rank.date})
      </div>}

      {data && !schools.length ? (
        <div className="rounded-2xl bg-white/80 border border-amber-300 p-8 text-center text-stone-600" data-testid="no-schools-linked">
          لم يتم ربط أي مدرسة {readOnly ? "بهذا المشرف" : "بحسابك"} بعد. يقوم المدير العام بتوزيع المدارس على المشرفين من صفحة «إدارة المستخدمين».
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <Stat tid="sum-schools" label="المدارس" value={sm?.schools ?? "…"} />
            <Stat tid="sum-activation" label={`نسبة التفعيل (${sm?.activated ?? 0} مفعّلة)`} value={`${sm?.activation_rate ?? 0}%`} tone="text-emerald-700" />
            <Stat tid="sum-rate" label="متوسط المشاركة" value={`${sm?.avg_rate ?? 0}%`} />
            <Stat tid="sum-hours" label="الساعات الفعلية" value={sm?.hours ?? 0} />
            <Stat tid="sum-opps" label="الفرص المنفذة" value={sm?.executed_opportunities ?? 0} />
            <Stat tid="sum-vols" label="المتطوعون" value={sm?.volunteers ?? 0} />
            <Stat tid="sum-alerts" label="مدارس بلا نشاط" value={sm?.alerts ?? 0} tone="text-red-700" />
            <Stat tid="sum-plans" label={`خطط مفتوحة (${sm?.closed_plans ?? 0} مغلقة)`} value={sm?.open_plans ?? 0} tone="text-amber-700" />
          </div>

          <div className="flex flex-wrap gap-2 border-b border-amber-400/20 items-end">
            {[["schools", "المدارس"], ["plans", `الخطط العلاجية (${openPlans})`], ["visits", `سجل المتابعات (${visits.length})`]].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-3 sm:px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-sky-600 text-sky-800 font-semibold" : "border-transparent text-stone-500"}`}>{l}</button>
            ))}
            {!readOnly && <div className="mr-auto mb-1 flex gap-1.5">
              <button data-testid="new-plan-btn" onClick={() => setPlanFor({})} className="px-3 py-1.5 rounded-xl text-xs border border-red-400/50 text-red-700 bg-red-50 flex items-center gap-1"><Stethoscope size={14} /> خطة علاجية</button>
              <button data-testid="new-visit-btn" onClick={() => setVisitFor({})} className="btn-gold px-3 py-1.5 rounded-xl text-xs flex items-center gap-1"><ClipboardCheck size={14} /> متابعة جديدة</button>
            </div>}
          </div>

          {tab === "schools" && (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="status-filter">
                {chips.map(([k, l, n]) => (
                  <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === k ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-300/50 text-stone-700"}`}>{l} ({n ?? 0})</button>
                ))}
              </div>
              <div className="relative max-w-md">
                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input data-testid="my-schools-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم المدرسة..." className="w-full pr-9 px-3 py-2 rounded-xl bg-white border border-sky-300/50 text-sm focus:outline-none" />
              </div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {shown.map((s) => <SchoolCard key={s.id} s={s} threshold={data.inactive_days_threshold} readOnly={readOnly} onVisit={setVisitFor} onMsg={setMsgFor} onPlan={setPlanFor} />)}
              </div>
              {data && !shown.length && <div className="text-center text-stone-500 text-sm py-6">لا توجد مدارس مطابقة</div>}
            </>
          )}
          {tab === "plans" && <RemedialPlans plans={plans} readOnly={readOnly} onChanged={load} />}
          {tab === "visits" && <VisitsLog visits={visits} readOnly={readOnly} onChanged={load} />}
        </>
      )}

      {visitFor && <VisitDialog schools={schools} defaultSchoolId={visitFor.id} onClose={() => setVisitFor(null)} onSaved={load} />}
      {msgFor && <MessageBank school={msgFor} onClose={() => setMsgFor(null)} />}
      {planFor && <PlanDialog schools={schools.filter((s) => !s.open_plans)} defaultSchoolId={planFor.id} onClose={() => setPlanFor(null)} onSaved={load} />}
    </div>
  );
}
